/* =========================================================================
   Settings → Bunny CDN / Email (SMTP) / Email routing / Stripe field definitions.

   Field names MIRROR the backend's CREDENTIAL_SCHEMA in
   src/services/platformCredentialService.js (media-platform). The backend
   validates and answers 422 + a per-field `errors` map; `secret` fields are
   never sent back, only whether they are set and where from.

   Email (SMTP) and Email routing are form-only: the backend copies .env in
   once and never falls back to it, so their source is always "database".

   Multi-tenant: a tenant admin edits the tenant's own copy, used whole or not
   at all. Until every required field is saved, the tenant keeps the Bunny /
   SMTP / Stripe account set up for it in SuperAdmin (else the platform's).
   ========================================================================= */

export const INTEGRATION_SECTIONS = [
  {
    key: "bunny_cdn",
    title: "Bunny CDN",
    intro: "Uploads, encoding and playback. Until all four required fields are saved, your account set up by the platform is used. Switching libraries does not move existing videos: keep the old library until they are re-uploaded. A creator's own Bunny Stream library still overrides these for that creator.",
    groups: [
      { key: "stream", title: "Bunny Stream", description: "Stream → your library → API" },
      { key: "storage", title: "Storage", description: "Images, subtitles and other files" },
    ],
    fields: [
      { name: "libraryId", group: "stream", label: "Library ID", help: "Video Library ID", placeholder: "123456" },
      { name: "streamApiKey", group: "stream", label: "API key", secret: true, help: "API Key (not the account key)" },
      { name: "pullzoneHostname", group: "stream", label: "CDN hostname", help: "Used for playback URLs", placeholder: "vz-xxxxxxxx-xxx.b-cdn.net", wide: true },
      { name: "imageStorageZone", group: "storage", label: "Image storage zone", placeholder: "my-images" },
      { name: "storagePassword", group: "storage", label: "Storage zone password", secret: true, help: "Zone → FTP & API Access → Password" },
      { name: "videoStorageZone", group: "storage", label: "Video storage zone", placeholder: "my-videos" },
      { name: "pullZone", group: "storage", label: "Pull zone", placeholder: "my-pullzone.b-cdn.net" },
      { name: "imageBaseUrl", group: "storage", label: "Image CDN base URL", placeholder: "https://my-images.b-cdn.net", wide: true },
    ],
  },
  {
    key: "smtp",
    title: "Email (SMTP)",
    intro: "Sign-up codes, receipts, support replies and campaigns. Until host, username, password and From address are saved, mail goes out through the account set up by the platform. For AWS SES use the region's SMTP endpoint and SMTP credentials.",
    groups: [
      { key: "server", title: "Server", description: "Where mail is sent from" },
      { key: "sender", title: "Sender", description: "What recipients see" },
    ],
    fields: [
      { name: "host", group: "server", label: "Host", placeholder: "email-smtp.us-east-1.amazonaws.com" },
      { name: "port", group: "server", label: "Port", help: "587 (STARTTLS) or 465 (TLS)", placeholder: "587" },
      { name: "username", group: "server", label: "Username" },
      { name: "password", group: "server", label: "Password", secret: true },
      { name: "fromAddress", group: "sender", label: "From address", help: "Must be a verified sender", placeholder: "no-reply@example.com" },
      { name: "fromName", group: "sender", label: "From name", placeholder: "My Streaming Service" },
      { name: "logoUrl", group: "sender", label: "Email logo", help: "Top of every email. If empty, Site identity → App logo is used", placeholder: "https://…/logo.png", wide: true, upload: true },
    ],
    // A real email through the saved settings, so the sender name and logo can be checked.
    sendTest: {
      title: "Send a test email",
      description: "Check delivery and how it looks",
      button: "Send test email",
      help: "Sent with the saved settings — the same server, From name and logo your subscribers' emails use. Real templates are filled with sample data.",
      // Keys mirror the backend's SMTP_TEST_TEMPLATES (src/services/smtpTestTemplates.js).
      templates: [
        { key: "basic", label: "Basic test" },
        { key: "forgot_password", label: "Password reset code" },
        { key: "email_verification", label: "Sign-up verification code" },
        { key: "welcome", label: "Subscriber welcome" },
        { key: "staff_invite", label: "Staff invite" },
        { key: "staff_password_reset", label: "Staff password reset" },
        { key: "creator_invite", label: "Creator invite" },
        { key: "support_reply", label: "Support reply" },
        { key: "announcement", label: "Announcement" },
        { key: "new_content", label: "New content alert" },
        { key: "payout_confirmation", label: "Creator payout confirmation" },
        { key: "submission_decision", label: "Content submission decision" },
      ],
    },
  },
  {
    key: "mail_routing",
    title: "Email routing",
    intro: "Department inboxes for your notifications. The executive CC is copied on administrative notifications such as new support tickets.",
    testable: false,
    // "Send test emails": one mail from each saved department address.
    sendTest: {
      title: "Send test emails",
      description: "One for each saved department address",
      button: "Send test emails",
      help: "Each mail replies to that department's address, and is sent from it when it shares the SMTP From address's domain (otherwise from the SMTP From address). The executive CC is not copied.",
    },
    groups: [
      { key: "departments", title: "Departments", description: "Who gets notified" },
      { key: "executive", title: "Executive", description: "Copied on administrative notifications" },
    ],
    fields: [
      { name: "supportEmail", group: "departments", label: "Support inbox", help: "New support tickets. Also the reply-to in welcome and support emails", placeholder: "support@example.com" },
      { name: "submissionsEmail", group: "departments", label: "Submissions inbox", help: "Content waiting for review. Not sent yet -- submission notifications are coming next", placeholder: "submissions@example.com" },
      { name: "subscribersEmail", group: "departments", label: "Subscribers sender", help: "Reply-to of subscriber announcements; also the From when it is on the SMTP From address's domain", placeholder: "subscribers@example.com" },
      { name: "executiveCc", group: "executive", label: "Executive CC", help: "Comma-separated", placeholder: "ceo@example.com, ops@example.com" },
    ],
  },
  {
    key: "stripe",
    title: "Stripe",
    intro: "Web subscriptions, buy/rent checkout and creator payouts. Until a secret key is saved, the Stripe account set up by the platform is used. Changing the key on a live site moves new payments to that Stripe account -- existing plans and prices must exist there too. Success URLs default to your website.",
    groups: [
      { key: "api", title: "API key", description: "Developers → API keys" },
      { key: "webhooks", title: "Webhooks", description: "Developers → Webhooks → your endpoint → Signing secret" },
      { key: "redirects", title: "Checkout redirects", description: "Where Stripe sends the viewer after paying" },
    ],
    fields: [
      { name: "secretKey", group: "api", label: "Secret key", secret: true, help: "sk_live_… / sk_test_…, or a restricted rk_… key", wide: true },
      { name: "publishableKey", group: "api", label: "Publishable key", help: "pk_live_… / pk_test_…, for card forms on your website", wide: true },
      { name: "webhookSecret", group: "webhooks", label: "Signing secret", secret: true, help: "Your account destination (payments)" },
      { name: "connectWebhookSecret", group: "webhooks", label: "Connect signing secret", secret: true, help: "Connected accounts destination (creator payouts)" },
      { name: "successUrl", group: "redirects", label: "Subscription success URL", placeholder: "https://example.com/thank-you", wide: true },
      { name: "buyRentSuccessUrl", group: "redirects", label: "Buy/rent success URL", placeholder: "https://example.com/thank-you", wide: true },
    ],
  },
];

export const INTEGRATION_KEYS = INTEGRATION_SECTIONS.map((s) => s.key);

/** Form state for non-secret fields from a masked GET; secrets always start empty. */
export const integrationFormFrom = (section, masked) =>
  Object.fromEntries(
    section.fields.map((f) => [f.name, f.secret ? "" : masked?.fields?.[f.name]?.value ?? ""])
  );

/**
 * What to PUT: non-secret fields whose value changed (an emptied field is sent
 * as "" so the backend clears it; Bunny / Stripe then fall back to .env), and secret fields only
 * when something was typed (blank means "keep the saved one").
 */
export const integrationPatch = (section, values, masked) => {
  const patch = {};
  for (const f of section.fields) {
    const value = String(values[f.name] ?? "").trim();
    if (f.secret) {
      if (value) patch[f.name] = String(values[f.name]);
      continue;
    }
    // The field starts with what is in effect (saved or .env); only an edit is saved.
    const shown = String(masked?.fields?.[f.name]?.value ?? "");
    if (value !== shown) patch[f.name] = value;
  }
  return patch;
};
